/** Per-property Emails tab — v1 js/emails-tab.js, same contract:
 *
 *   - GET /api/mail/accounts?org_id&user_id&is_admin → {accounts:[{id,email,user_id}]}
 *   - Gmail live search: GET /api/mail/threads?account_id&q&limit=25 where
 *     q = "street" (+ OR from:seller OR to:seller when the seller email is a
 *     real third party — matching your own mailbox would return everything)
 *   - CRM send log:      GET /api/mail/for-lead?lead_key&org_id → {emails}
 *   - Lazy body:         GET /api/mail/message?account_id&id → {body|snippet}
 *   - STRICT split: Gmail search also hits attachment contents and quoted
 *     text, so messages whose visible fields don't contain the street/seller
 *     hide behind "show more matches" (v1 _hit, verbatim).
 *
 * SENDING (POST /api/mail/send) is a live-fire action gated by liveFire
 * (Phase B). sendMail is HONEST by construction — see below.
 */
import { backendFetch, type BackendOptions } from "../../lib/backend";

export interface MailAccount {
  id: string;
  email: string;
  userId: string | null;
}

export interface MailMsg {
  id: string;
  subject: string;
  from: string;
  to: string;
  /** gmail Date header (threads) or sent_at ISO (CRM log) — preformatted */
  date: string;
  snippet: string;
  /** 'out' = Sent pill; anything else renders Received */
  direction: string | null;
  /** true = came from the CRM send log, shows the "CRM log" pill */
  fromLog: boolean;
  /** Emails PAGE rows highlight unread (v1 msg.unread) */
  unread: boolean;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

export function narrowAccount(raw: unknown): MailAccount | null {
  if (!isObj(raw)) return null;
  const id = str(raw["id"]);
  const email = str(raw["email"]);
  if (!id || !email) return null;
  return { id, email, userId: str(raw["user_id"]) || null };
}

/** v1 row date formats: gmail Date header loses its trailing tz garbage; log
 * sent_at renders "YYYY-MM-DD HH:MM". */
export const fmtGmailDate = (d: unknown): string => str(d).replace(/\s[+-]\d{4}.*$/, "");
export const fmtLogDate = (d: unknown): string => str(d).slice(0, 16).replace("T", " ");

export function narrowGmailMsg(raw: unknown): MailMsg | null {
  if (!isObj(raw)) return null;
  const id = str(raw["id"]);
  if (!id) return null;
  return {
    id,
    subject: str(raw["subject"]),
    from: str(raw["from"]),
    to: str(raw["to"]),
    date: fmtGmailDate(raw["date"]),
    snippet: str(raw["snippet"]),
    direction: str(raw["direction"]) || null,
    fromLog: false,
    unread: raw["unread"] === true,
  };
}

/** CRM log rows: sender is the connected account, direction is 'out'. */
export function narrowLogMsg(raw: unknown): MailMsg | null {
  if (!isObj(raw)) return null;
  const id = str(raw["id"]);
  if (!id) return null;
  return {
    id,
    subject: str(raw["subject"]),
    from: str(raw["from"]) || str(raw["account_email"]),
    to: str(raw["to"]),
    date: fmtLogDate(raw["sent_at"]),
    snippet: str(raw["snippet"]),
    direction: str(raw["direction"]) || "out",
    fromLog: true,
    unread: false,
  };
}

// ---- account + query building (v1 leadMailLoad, verbatim) --------------------

/** v1: my mailbox if I have one, else the org's first. */
export function pickAccount(accounts: MailAccount[], userId: string | null): MailAccount | null {
  return accounts.find((a) => a.userId !== null && a.userId === userId) ?? accounts[0] ?? null;
}

/** The seller email only joins the query when it's a real third party. */
export function buildLeadMailQuery(address: string, sellerEmail: string, accountEmail: string): { q: string; sellerUsed: boolean } {
  const street = (address || "").split(",")[0]?.trim() ?? "";
  const seller = (sellerEmail || "").trim();
  const sellerOk = !!seller && seller.toLowerCase() !== (accountEmail || "").toLowerCase();
  let q = '"' + street + '"';
  if (sellerOk) q += " OR from:" + seller + " OR to:" + seller;
  return { q, sellerUsed: sellerOk };
}

/** v1 _hit: does the street (or the used seller email) appear in the VISIBLE
 * fields? Loose matches (attachments/quoted text only) hide behind a toggle. */
export function splitStrict(msgs: MailMsg[], address: string, sellerUsed: boolean, sellerEmail: string): { direct: MailMsg[]; loose: MailMsg[] } {
  const street = ((address || "").split(",")[0] ?? "").trim().toLowerCase();
  const seller = (sellerUsed && sellerEmail ? sellerEmail : "").toLowerCase();
  const hit = (m: MailMsg) => {
    const t = (m.subject + " " + m.snippet + " " + m.from + " " + m.to).toLowerCase();
    return (!!street && t.includes(street)) || (!!seller && t.includes(seller));
  };
  return { direct: msgs.filter(hit), loose: msgs.filter((m) => !hit(m)) };
}

/** Log entries not already present in the Gmail results (by id). */
export const logOnly = (log: MailMsg[], msgs: MailMsg[]): MailMsg[] =>
  log.filter((e) => !msgs.some((m) => m.id === e.id));

/** v1 leadMailContacts: known addresses for the compose picker — seller,
 * listing agent, everyone seen in from/to — minus the sending mailbox, max 10.
 * Returns [email, label] pairs. */
export function mailContacts(
  msgs: MailMsg[],
  sellerEmail: string,
  agentEmail: string,
  accountEmail: string,
): [string, string][] {
  const out: [string, string][] = [];
  const seen = new Set<string>();
  const add = (emRaw: unknown, label: string) => {
    const em = String(emRaw ?? "").trim();
    if (!em || !em.includes("@")) return;
    const k = em.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push([em, label]);
  };
  add(sellerEmail, "Seller");
  add(agentEmail, "Listing agent");
  for (const m of msgs) {
    const found = (m.from + "," + m.to).match(/[^<,;\s"']+@[^>,;\s"']+/g) ?? [];
    for (const em of found) add(em.replace(/[<>]/g, ""), "From emails");
  }
  const acc = (accountEmail || "").toLowerCase();
  return out.filter(([em]) => em.toLowerCase() !== acc).slice(0, 10);
}

/** Seller email for a lead: v1 reads edits.seller.email (an object field). */
export function sellerEmailOf(fields: Record<string, unknown> | undefined): string {
  const s = fields?.["seller"];
  if (isObj(s) && typeof s["email"] === "string") return s["email"].trim();
  return "";
}

/** Listing-agent email: v1 compose reads edits.sl_aemail. */
export function agentEmailOf(fields: Record<string, unknown> | undefined): string {
  const v = fields?.["sl_aemail"];
  return typeof v === "string" ? v.trim() : "";
}

// ---- fetchers ----------------------------------------------------------------

export async function fetchMailAccounts(
  token: string | null,
  orgId: string,
  userId: string,
  isAdmin: boolean,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<MailAccount[]> {
  const j = await backendFetch<{ accounts?: unknown }>(
    token,
    `/api/mail/accounts?org_id=${encodeURIComponent(orgId)}&user_id=${encodeURIComponent(userId)}&is_admin=${isAdmin ? "true" : "false"}`,
    { timeoutMs: 30000, ...opts },
  );
  return Array.isArray(j?.accounts) ? (j.accounts as unknown[]).map(narrowAccount).filter((a): a is MailAccount => a !== null) : [];
}

/** 401 from Gmail = token needs a reconnect — surfaced, not fatal. */
export async function fetchLeadThreads(
  token: string | null,
  accountId: string,
  q: string,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<{ messages: MailMsg[]; reconnect: boolean }> {
  try {
    const j = await backendFetch<{ messages?: unknown }>(
      token,
      `/api/mail/threads?account_id=${encodeURIComponent(accountId)}&q=${encodeURIComponent(q)}&limit=25`,
      { timeoutMs: 45000, ...opts },
    );
    return {
      messages: Array.isArray(j?.messages) ? (j.messages as unknown[]).map(narrowGmailMsg).filter((m): m is MailMsg => m !== null) : [],
      reconnect: false,
    };
  } catch (e) {
    if ((e as { status?: number })?.status === 401) return { messages: [], reconnect: true };
    return { messages: [], reconnect: false }; // v1 .catch(()=>({messages:[]}))
  }
}

export async function fetchLeadLog(
  token: string | null,
  leadKey: string,
  orgId: string,
  opts?: Pick<BackendOptions, "signal">,
): Promise<MailMsg[]> {
  try {
    const j = await backendFetch<{ emails?: unknown }>(
      token,
      `/api/mail/for-lead?lead_key=${encodeURIComponent(leadKey)}&org_id=${encodeURIComponent(orgId)}`,
      { timeoutMs: 30000, ...opts },
    );
    return Array.isArray(j?.emails) ? (j.emails as unknown[]).map(narrowLogMsg).filter((m): m is MailMsg => m !== null) : [];
  } catch (e) {
    // v1 did .catch(()=>({emails:[]})), which rendered "No emails found for
    // this property yet" on a Gmail 500 or a thread-search timeout. Empty and
    // failed are not the same answer, and the wrong one tells you a
    // conversation doesn't exist. Rethrow so the caller can say so; a total
    // outage is already handled separately upstream.
    throw e;
  }
}

// ---- Emails PAGE (v1 dialer-comms.js mail* fns) -------------------------------

/** v1 who(): pull the display name out of `"Name" <email>`; else strip <>. */
export const mailWho = (s: string): string => {
  const m = String(s || "").match(/^\s*"?([^"<]+?)"?\s*</);
  return m?.[1]?.trim() ?? String(s || "").replace(/[<>]/g, "").trim();
};

/** v1 list-row date: drop "(TZ)" suffix, keep the first 4 tokens. */
export const mailListDate = (d: string): string =>
  String(d || "").replace(/\s*\([^)]*\)\s*$/, "").split(" ").slice(0, 4).join(" ");

/** v1 mailOpenMsg reply target: the <email> from the From header, else raw. */
export const replyToOf = (from: string): string => (String(from || "").match(/<([^>]+)>/) ?? [])[1] ?? String(from || "");

/** v1 mailSendReply subject rule. */
export const reSubject = (subject: string): string => (/^re:/i.test(subject) ? subject : "Re: " + subject);

/** v1 mailLoadThreads: the FULL inbox (no q), limit 30; 'gmail_reconnect_required'
 * renders the amber reconnect copy, other errors show their detail. */
export async function fetchMailInbox(
  token: string | null,
  accountId: string,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<{ messages: MailMsg[]; reconnect: boolean; detail: string | null }> {
  try {
    const j = await backendFetch<{ messages?: unknown }>(
      token,
      `/api/mail/threads?account_id=${encodeURIComponent(accountId)}&limit=30`,
      { timeoutMs: 45000, ...opts },
    );
    return {
      messages: Array.isArray(j?.messages) ? (j.messages as unknown[]).map(narrowGmailMsg).filter((m): m is MailMsg => m !== null) : [],
      reconnect: false,
      detail: null,
    };
  } catch (e) {
    const be = e as { status?: number; detail?: string };
    if (be?.status === 401 || be?.detail === "gmail_reconnect_required") {
      return { messages: [], reconnect: true, detail: null };
    }
    return { messages: [], reconnect: false, detail: String(be?.detail ?? "Could not load mail").slice(0, 160) };
  }
}

/** v1 mailOpenMsg: the full message for the reader pane. */
export async function fetchMailMessageFull(
  token: string | null,
  accountId: string,
  id: string,
  opts?: Pick<BackendOptions, "signal">,
): Promise<{ subject: string; from: string; to: string; date: string; body: string; threadId: string | null } | null> {
  try {
    const j = await backendFetch<Record<string, unknown>>(
      token,
      `/api/mail/message?account_id=${encodeURIComponent(accountId)}&id=${encodeURIComponent(id)}`,
      { timeoutMs: 30000, ...opts },
    );
    return {
      subject: str(j?.["subject"]),
      from: str(j?.["from"]),
      to: str(j?.["to"]),
      date: str(j?.["date"]),
      body: str(j?.["body"]) || str(j?.["snippet"]) || "(no content)",
      // v1 mailSendReply threads the reply via m.threadId — carry it through
      threadId: str(j?.["threadId"]) || str(j?.["thread_id"]) || null,
    };
  } catch {
    return null;
  }
}

export async function fetchMailBody(
  token: string | null,
  accountId: string,
  id: string,
  opts?: Pick<BackendOptions, "signal">,
): Promise<string> {
  const j = await backendFetch<{ body?: unknown; snippet?: unknown }>(
    token,
    `/api/mail/message?account_id=${encodeURIComponent(accountId)}&id=${encodeURIComponent(id)}`,
    { timeoutMs: 30000, ...opts },
  );
  return str(j?.body) || str(j?.snippet) || "(no content)";
}

export const MAIL_MSG = {
  noMailbox: "No mailbox connected yet.",
  connectPhaseB: "Connect Gmail — unlocks in the live-fire session",
  sendPhaseB: "Sending unlocks in the live-fire session",
  reconnect: "Gmail session expired — reconnect in v1 (Comms → Email) and refresh.",
  bodyFail: "Could not load message body.",
  empty: "No emails found for this property yet.",
  loading: "Loading emails…",
  sendNeedsTo: "Add a recipient before sending.",
  sending: "Sending…",
  sentOk: "Sent ✓",
};

export interface MailSendPayload {
  accountId: string;
  to: string;
  subject: string;
  body: string;
  threadId?: string | null;
  leadKey?: string | null;
  /** one key per draft (idempotency): same key twice => ONE email */
  idemKey?: string | null;
}

/** POST /api/mail/send — v1 dialer-comms `mailSend` payload, verbatim.
 *
 * HONEST BY CONSTRUCTION (the v1 green-toast-lie must not recur): this resolves
 * ONLY when the server returns 2xx. Any non-2xx throws BackendError carrying
 * the server's detail, so the UI can never show a false "sent". SINGLE attempt
 * for send safety — backendFetch's only retry is the asleep-gateway 502/503
 * wake, which by definition never reached the app, so it cannot double-send;
 * every other failure surfaces to the caller instead of being retried (a blind
 * retry on a send risks a duplicate email). */
export async function sendMail(
  token: string | null,
  orgId: string,
  p: MailSendPayload,
  opts: BackendOptions = {},
): Promise<void> {
  await backendFetch(token, "/api/mail/send", {
    method: "POST",
    body: {
      account_id: p.accountId,
      to: p.to,
      subject: p.subject,
      body: p.body,
      thread_id: p.threadId ?? null,
      org_id: orgId,
      lead_key: p.leadKey ?? null,
      idem_key: p.idemKey ?? null,
    },
    timeoutMs: 30000,
    ...(opts.onWake ? { onWake: opts.onWake } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}
